import type {
  PublicBoost,
  VoucherResponseDto,
  BonusDefinition,
} from '~/domains/promotions/types';
import { PromoType } from '~/domains/promotions/types';
import {
  parseDepositBonusToCardProps,
  parseVoucherToCardProps,
  parseBoostToCardProps,
} from '~/domains/promotions/utils/transformers';

import { usePromotionsContext } from '../../context/PromotionsContext';

import PromoTermsModal, { type PromoTermsModalProps } from './PromoTermsModal';

/**
 * Discriminated payload describing which entity's terms to show.
 * Exported separately from `ConnectedPromoTermsModalProps` so the
 * modal context can compose it with `{ type: 'PromoTermsModal' }`
 * without `Omit` collapsing the discriminator.
 */
export type ConnectedPromoTermsModalPayload =
  | {
      mode: PromoType.BOOST;
      boost: PublicBoost;
      onApply?: (boost: PublicBoost) => void;
    }
  | {
      mode: PromoType.VOUCHER;
      voucher: VoucherResponseDto;
      onApply?: (voucher: VoucherResponseDto) => void;
    }
  | {
      mode: PromoType.DEPOSIT_BONUS;
      bonus: BonusDefinition;
      onApply?: (bonus: BonusDefinition) => void;
    };

export type ConnectedPromoTermsModalProps = ConnectedPromoTermsModalPayload & {
  isOpen: boolean;
  onClose: () => void;
};


/**
 * Maps a discriminated `boost | voucher | bonus` payload into the
 * flat `PromoTermsModalProps` shape. Pure logic — no hooks, no
 * context. Registered in `PromotionsModalsContext` and dispatched by
 * `PromotionsModals.tsx` under `type: 'PromoTermsModal'`.
 */
function ConnectedPromoTermsModal(props: ConnectedPromoTermsModalProps) {
  const { isOpen, onClose, mode } = props;
  const { selectedBoost, selectedVoucher } = usePromotionsContext();

  // @TODO: Refactor using tranformer?
  const displayProps: PromoTermsModalProps = (() => {
    switch (mode) {
      case PromoType.BOOST: {
        const { boost, onApply } = props;
        const isSelected = selectedBoost?.id === boost.id;

        return {
          isOpen,
          onClose,
          terms: boost.terms,
          onAction: onApply ? () => onApply(boost) : undefined,

          promoCardProps: parseBoostToCardProps(boost, isSelected),
        };
      }
      case PromoType.VOUCHER: {
        const { voucher, onApply } = props;
        const isSelected = selectedVoucher?.voucherId === voucher.voucherId;

        return {
          isOpen,
          onClose,
          terms: voucher.terms,
          onAction: onApply ? () => onApply(voucher) : undefined,

          promoCardProps: parseVoucherToCardProps(voucher, isSelected),
        };
      }

      // @TODO: This needs to be fixed when invoked from Deposit Modal (broken promoCard value)
      case PromoType.DEPOSIT_BONUS: {
        const { bonus, onApply } = props;

        return {
          isOpen,
          onClose,
          terms: bonus.terms,
          onAction: onApply ? () => onApply(bonus) : undefined,

          promoCardProps: parseDepositBonusToCardProps(bonus),
        };
      }
      default: {
        return null;
      }
    }
  })();

  return <PromoTermsModal {...displayProps} />;
}

export default ConnectedPromoTermsModal;
